// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Button, Group, Paper, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import type { JSX } from 'react';

/**
 * TODO(devices-dme): Durable Medical Equipment is not modeled in FHIR today.
 * This section is a visual stub — rendered to match the design but driven by
 * empty data and disabled actions. A follow-up PR will introduce the
 * `DeviceRequest` + `Device` + `SupplyDelivery` resources, wire them to
 * `usePatientDmeOrders`, and enable the Order / View Script / Renew actions.
 *
 * Columns per the mock: Modality, Code(s), Condition, Status, Ordered by,
 * Order Date, Last Filled, Expires, Actions (View Script, Renew).
 */
export function DmeSection(): JSX.Element {
  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>DME</Title>
        <Group gap="sm">
          <TextInput
            placeholder="Search…"
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={240}
            disabled
            aria-label="Search DME orders"
          />
          <Button leftSection={<IconPlus size={16} />} disabled title="DME ordering coming in a follow-up PR">
            Order
          </Button>
        </Group>
      </Group>

      <Box style={{ overflowX: 'auto' }}>
        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Modality</Table.Th>
              <Table.Th>Code(s)</Table.Th>
              <Table.Th>Condition</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Ordered by</Table.Th>
              <Table.Th>Order Date</Table.Th>
              <Table.Th>Last Filled</Table.Th>
              <Table.Th>Expires</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td colSpan={9}>
                <Stack align="center" py="md" gap={4}>
                  <Text c="dimmed">DME orders are not yet tracked for patients.</Text>
                  <Text size="xs" c="dimmed">
                    Wiring DeviceRequest / Device / SupplyDelivery in a follow-up PR.
                  </Text>
                </Stack>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Box>
    </Paper>
  );
}
